import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Shield, FileText, CheckCircle, AlertTriangle, Loader2,
  ChevronDown, ChevronUp, PenTool
} from 'lucide-react';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverNDA = () => {
  const navigate = useNavigate();
  const [ndaContent, setNdaContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [error, setError] = useState('');
  const [applicationInfo, setApplicationInfo] = useState(null);
  
  const [agreements, setAgreements] = useState({
    terms: false,
    confidentiality: false,
    codeOfConduct: false
  });
  
  const [signature, setSignature] = useState({
    fullName: '',
    digitalSignature: ''
  });

  useEffect(() => {
    const storedApp = localStorage.getItem('observer_application');
    if (!storedApp) {
      navigate('/observer/register');
      return;
    }
    setApplicationInfo(JSON.parse(storedApp));
    loadNDAContent();
  }, [navigate]);

  const loadNDAContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/observer/nda-content`);
      setNdaContent(response.data);
    } catch (err) {
      setError('Failed to load NDA content');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const allAgreed = agreements.terms && agreements.confidentiality && agreements.codeOfConduct;
  const signatureValid = signature.fullName.trim().length > 2 && signature.digitalSignature.trim().length > 2;

  const handleSubmit = async () => {
    if (!allAgreed || !signatureValid) {
      setError('Please complete all agreements and provide your signature');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/observer/sign-nda`,
        null,
        {
          params: {
            application_id: applicationInfo.application_id,
            full_name: signature.fullName,
            agreed_to_terms: agreements.terms,
            agreed_to_confidentiality: agreements.confidentiality,
            agreed_to_code_of_conduct: agreements.codeOfConduct,
            digital_signature: signature.digitalSignature
          }
        }
      );
      
      if (response.data.success) {
        // Clear stored application data
        localStorage.removeItem('observer_application');
        navigate('/observer/application-status', { 
          state: { email: applicationInfo.email } 
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit NDA');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
      <Navigation />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Non-Disclosure Agreement</h1>
            <p className="text-gray-600 mt-2">Please review and sign the following agreements</p>
            {applicationInfo && (
              <p className="text-sm text-purple-600 mt-2">Applicant: {applicationInfo.name}</p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* NDA Sections */}
          <Card className="shadow-xl border-0 mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                {ndaContent?.sections?.[0]?.title || 'Non-Disclosure Agreement'}
              </h2>
              
              <div className="space-y-3">
                {ndaContent?.sections?.map((section, idx) => (
                  <div key={idx} className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection(idx)}
                      className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
                    >
                      <span className="font-medium text-gray-900">{section.title}</span>
                      {expandedSections[idx] ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    {expandedSections[idx] && (
                      <div className="px-4 py-3 bg-white">
                        <p className="text-gray-700 whitespace-pre-line text-sm">{section.content}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Code of Conduct */}
          <Card className="shadow-xl border-0 mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {ndaContent?.code_of_conduct?.title || 'Observer Code of Conduct'}
              </h2>
              <ul className="space-y-2">
                {ndaContent?.code_of_conduct?.points?.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{point}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Terms of Service */}
          <Card className="shadow-xl border-0 mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {ndaContent?.terms_of_service?.title || 'Terms of Service'}
              </h2>
              <ul className="space-y-2">
                {ndaContent?.terms_of_service?.points?.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{point}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Agreements */}
          <Card className="shadow-xl border-0 mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Agreements</h2>
              
              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={agreements.terms}
                    onCheckedChange={(checked) => setAgreements(prev => ({ ...prev, terms: checked }))}
                    className="mt-1"
                  />
                  <span className="text-gray-700">
                    I have read and agree to the <strong>Terms of Service</strong> and understand my responsibilities as an Observer.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={agreements.confidentiality}
                    onCheckedChange={(checked) => setAgreements(prev => ({ ...prev, confidentiality: checked }))}
                    className="mt-1"
                  />
                  <span className="text-gray-700">
                    I agree to maintain strict <strong>confidentiality</strong> of all information I access through the platform and will not disclose any personal information about children or families.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={agreements.codeOfConduct}
                    onCheckedChange={(checked) => setAgreements(prev => ({ ...prev, codeOfConduct: checked }))}
                    className="mt-1"
                  />
                  <span className="text-gray-700">
                    I agree to follow the <strong>Observer Code of Conduct</strong> and understand that violations may result in immediate termination.
                  </span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Digital Signature */}
          <Card className="shadow-xl border-0 mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <PenTool className="w-5 h-5 text-purple-600" />
                Digital Signature
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Legal Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={signature.fullName}
                    onChange={(e) => setSignature(prev => ({ ...prev, fullName: e.target.value }))}
                    placeholder="Enter your full legal name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type Your Signature <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={signature.digitalSignature}
                    onChange={(e) => setSignature(prev => ({ ...prev, digitalSignature: e.target.value }))}
                    placeholder="Type your name as signature"
                    className="font-cursive text-lg italic"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    By typing your name above, you acknowledge that this constitutes your legal electronic signature.
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>Date:</strong> {new Date().toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => navigate('/observer/register')}
            >
              Back to Registration
            </Button>
            
            <Button
              onClick={handleSubmit}
              disabled={!allAgreed || !signatureValid || submitting}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Sign & Submit
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObserverNDA;
